// Soft ground transitions. Where two grounds meet, the one higher in PRIORITY spills a ragged,
// faded edge of its texture onto its neighbour (grass over dirt, sand over water). Overlays are
// 8 masked frames per ground (4 sides, 4 outer corners, 4 wobble variants each) in one canvas
// texture, drawn with a Blitter per chunk. Ruin and dungeon floors keep their hard edges.
import Phaser from 'phaser';
import { CHUNK, G, GROUNDS, TILE } from '../../shared/content.ts';

const KEY = 'ground-edges';
/** Higher spills onto lower; missing = never blends either way. */
const PRIORITY: Partial<Record<number, number>> = {
  [G.DEEP]: 1, [G.WATER]: 2, [G.SHALLOW]: 3, [G.SAND]: 4, [G.MUD]: 5, [G.FEN]: 6, [G.DIRT]: 7,
  [G.ASH]: 8, [G.CINDER]: 9, [G.ROCK]: 10, [G.HIGH]: 11, [G.GRASS]: 12, [G.MEADOW]: 13,
};
// Neighbour offsets in mask order: sides E S W N, then corners SE SW NW NE.
const SIDES: [number, number][] = [[1, 0], [0, 1], [-1, 0], [0, -1]];
const CORNERS: [number, number][] = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
const VARIANTS = 4;
const REACH = 26; // how far an edge spills in, pixels

export class GroundEdges {
  private ready = false;

  constructor(private scene: Phaser.Scene, private groundFrames: Record<number, number[]>) {}

  /** Build the overlay atlas from the loaded ground sheet. */
  build(): void {
    const src = this.scene.textures.get('ground').getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const ids = GROUNDS.map((g) => g.id).filter((id) => PRIORITY[id] !== undefined);
    const cols = 8 * VARIANTS;
    const P = TILE + 2 * PAD;
    const tex = this.scene.textures.createCanvas(KEY, cols * P, ids.length * P)!;
    const ctx = tex.getContext();
    const masks: HTMLCanvasElement[] = [];
    for (let v = 0; v < VARIANTS; v++) for (let m = 0; m < 8; m++) masks.push(makeMask(m, v));
    const cell = document.createElement('canvas');
    cell.width = cell.height = TILE;
    const cc = cell.getContext('2d')!;
    ids.forEach((id, row) => {
      const f = this.groundFrames[id]?.[0] ?? 0;
      masks.forEach((mask, k) => {
        cc.globalCompositeOperation = 'source-over';
        cc.clearRect(0, 0, TILE, TILE);
        cc.drawImage(src, f * TILE, 0, TILE, TILE, 0, 0, TILE, TILE);
        cc.globalCompositeOperation = 'destination-in';
        cc.drawImage(mask, 0, 0);
        extrudeInto(ctx, cell, 0, 0, k * P + PAD, row * P + PAD);
        tex.add(`${id}:${k}`, 0, k * P + PAD, row * P + PAD, TILE, TILE);
      });
    });
    tex.refresh();
    this.ready = true;
  }

  /** One Blitter of overlays for a chunk. `groundAt` answers for neighbouring chunks too (-1 = unknown). */
  draw(cx: number, cy: number, groundAt: (tx: number, ty: number) => number): Phaser.GameObjects.Blitter | null {
    if (!this.ready) return null;
    const bl = this.scene.add.blitter(cx * CHUNK * TILE, cy * CHUNK * TILE, KEY).setDepth(-1e7 + 1);
    for (let y = 0; y < CHUNK; y++) {
      for (let x = 0; x < CHUNK; x++) {
        const tx = cx * CHUNK + x, ty = cy * CHUNK + y;
        const own = PRIORITY[groundAt(tx, ty)];
        if (own === undefined) continue;
        // Gather which higher grounds touch this tile, on which sides and corners.
        const spills = new Map<number, number>(); // ground -> 8-bit mask
        const add = (g: number, bit: number) => spills.set(g, (spills.get(g) ?? 0) | (1 << bit));
        SIDES.forEach(([dx, dy], k) => {
          const g = groundAt(tx + dx, ty + dy);
          if ((PRIORITY[g] ?? -1) > own) add(g, k);
        });
        CORNERS.forEach(([dx, dy], k) => {
          const g = groundAt(tx + dx, ty + dy);
          if ((PRIORITY[g] ?? -1) > own) add(g, 4 + k);
        });
        if (!spills.size) continue;
        const v = ((tx * 73856093) ^ (ty * 19349663)) >>> 0;
        for (const g of [...spills.keys()].sort((a, b) => PRIORITY[a]! - PRIORITY[b]!)) {
          const m = spills.get(g)!;
          for (let k = 0; k < 8; k++) {
            if (!(m & (1 << k))) continue;
            // A corner only shows when neither side next to it already spills that ground.
            if (k >= 4) {
              const [s1, s2] = [[0, 1], [1, 2], [2, 3], [3, 0]][k - 4];
              if (m & (1 << s1) || m & (1 << s2)) continue;
            }
            bl.create(x * TILE, y * TILE, `${g}:${((v >>> k) % VARIANTS) * 8 + k}`);
          }
        }
      }
    }
    return bl;
  }
}

/** Padding around each atlas frame, filled with the frame's own edge pixels so texture filtering
 *  at a tile's border never samples its neighbour in the sheet (that shows as faint grid lines). */
export const PAD = 2;

/** Draw a TILE-sized frame from `src` at (dx, dy) on `ctx`, with its border pixels smeared PAD out. */
export function extrudeInto(ctx: CanvasRenderingContext2D, src: CanvasImageSource, sx: number, sy: number, dx: number, dy: number): void {
  const T = TILE;
  ctx.drawImage(src, sx, sy, 1, T, dx - PAD, dy, PAD, T);
  ctx.drawImage(src, sx + T - 1, sy, 1, T, dx + T, dy, PAD, T);
  ctx.drawImage(src, sx, sy, T, 1, dx, dy - PAD, T, PAD);
  ctx.drawImage(src, sx, sy + T - 1, T, 1, dx, dy + T, T, PAD);
  for (const [cx, cy] of [[0, 0], [T - 1, 0], [0, T - 1], [T - 1, T - 1]]) {
    ctx.drawImage(src, sx + cx, sy + cy, 1, 1, dx + (cx ? T : -PAD), dy + (cy ? T : -PAD), PAD, PAD);
  }
  ctx.drawImage(src, sx, sy, T, T, dx, dy, T, T);
}

/** The ground sheet (one row of TILE frames) copied with PAD around every frame, for the tilemaps:
 *  use it as a tileset with margin PAD and spacing 2 * PAD. */
export function extrudedGround(scene: Phaser.Scene, key: string, frames: number): void {
  const src = scene.textures.get('ground').getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const P = TILE + 2 * PAD;
  const tex = scene.textures.createCanvas(key, frames * P, P)!;
  const ctx = tex.getContext();
  for (let f = 0; f < frames; f++) extrudeInto(ctx, src, f * TILE, 0, f * P + PAD, PAD);
  tex.refresh();
}

/** Alpha mask for one side (0-3: E S W N) or outer corner (4-7: SE SW NW NE), with a wobbly edge. */
function makeMask(m: number, variant: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = TILE;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(TILE, TILE);
  const seed = m * 7 + variant * 131 + 1;
  // Wobble along the edge: harmonics that are zero at both ends of the tile (u = 0 and 1), so
  // neighbouring tiles' edges meet at the same depth whichever variants they use.
  const ph = [seed * 1.7, seed * 2.9, seed * 0.6];
  const wob = (u: number) => Math.sin(Math.PI * u) * 3.5 * Math.sin(ph[0]) + Math.sin(2 * Math.PI * u) * 3.5 * Math.cos(ph[1]) + Math.sin(3 * Math.PI * u) * 2.6 * Math.sin(ph[2])
    + Math.sin(5 * Math.PI * u) * 1.6 * Math.cos(ph[0] + ph[2]) + Math.sin(8 * Math.PI * u) * 1.1;
  for (let y = 0; y < TILE; y++) {
    for (let x = 0; x < TILE; x++) {
      let d: number; // distance in from the spilling edge or corner
      let along: number; // 0..1 along the edge
      if (m < 4) {
        d = [TILE - 1 - x, TILE - 1 - y, x, y][m];
        along = (m % 2 === 0 ? y + 0.5 : x + 0.5) / TILE;
      } else {
        const [cx, cy] = [[TILE, TILE], [0, TILE], [0, 0], [TILE, 0]][m - 4];
        d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
        along = Math.atan2(Math.abs(y + 0.5 - cy), Math.abs(x + 0.5 - cx)) / (Math.PI / 2);
      }
      const edge = REACH * 0.62 + wob(along);
      // Solid near the edge, a short soft fade, then nothing; a little dither in the fade.
      let a = 1 - smooth((d - edge + 4) / 8);
      if (a > 0 && a < 1) a = Math.max(0, Math.min(1, a + (hash(x, y, seed) - 0.5) * 0.25));
      img.data[(y * TILE + x) * 4 + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
const hash = (x: number, y: number, s: number) => {
  const h = Math.sin(x * 12.9898 + y * 78.233 + s * 37.719) * 43758.5453;
  return h - Math.floor(h);
};
